import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { StatusDot } from '../components/StatusDot'
import { Metric } from '../components/Metric'
import { api, type ApiError, type DashboardSnapshot, type Deliverability, type DeliverabilityRateBlock, type ExportStatus, type SyncRun, type ValidationCoverage, type WebhookAlertsResponse, type WebhookHealth } from '../api/client'
import { fmtDateTime, fmtInt, fmtRelative } from '../lib/format'
import { DailyChangePanel, SyncHealthPanel } from './Dashboard'

export function SyncPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['sync_runs'],
    queryFn: () => api.get<{ items: SyncRun[]; stats: { last_24h_added: number; last_24h_removed: number; errors: number } }>('/api/sync'),
    refetchInterval: 15_000,
  })

  const trigger = useMutation({
    mutationFn: () => api.post('/api/sync/run'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sync_runs'] }),
  })

  const exportStatus = useQuery({
    queryKey: ['export_status'],
    queryFn: () => api.get<ExportStatus>('/api/sync/export'),
    refetchInterval: (q) => {
      const s = q.state.data?.request?.status
      return s === 'pending' || s === 'running' ? 3_000 : false
    },
  })

  const runExport = useMutation({
    mutationFn: () => api.post('/api/sync/export'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['export_status'] }),
  })

  const exp = exportStatus.data
  const reqStatus = exp?.request?.status
  const exportInFlight = reqStatus === 'pending' || reqStatus === 'running'

  const coverage = useQuery({
    queryKey: ['validation_coverage'],
    queryFn: () => api.get<ValidationCoverage>('/api/sync/validation-coverage'),
    refetchInterval: 30_000,
  })

  const deliverability = useQuery({
    queryKey: ['deliverability'],
    queryFn: () => api.get<Deliverability>('/api/sync/deliverability'),
    refetchInterval: 30_000,
  })

  const webhookHealth = useQuery({
    queryKey: ['webhook_health'],
    queryFn: () => api.get<WebhookHealth>('/api/sync/webhook-health'),
    refetchInterval: 30_000,
  })

  const webhookAlerts = useQuery({
    queryKey: ['webhook_alerts'],
    queryFn: () => api.get<WebhookAlertsResponse>('/api/sync/webhook-alerts'),
    refetchInterval: 30_000,
  })

  // Dashboard snapshot powers the SyncHealthPanel (webhook freshness per pub,
  // csv reconciliation clock, stale-publications). Moved here from the
  // Overview page 2026-04-21 so all pipeline telemetry lives in one place.
  const dash = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardSnapshot>('/api/dashboard'),
    refetchInterval: 30_000,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Telemetry</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Sync Health
          </h1>
        </div>
        <Button
          onClick={() => trigger.mutate()}
          disabled={trigger.isPending}
          size="lg"
        >
          {trigger.isPending ? 'Running…' : 'Run Mailgun Sync Now'}
        </Button>
      </header>

      {webhookAlerts.data?.active && webhookAlerts.data.active.length > 0 && (
        <AlertBanner alerts={webhookAlerts.data.active} />
      )}

      {dash.data && <SyncHealthPanel data={dash.data.sync_health} />}

      {dash.data?.sync_health.csv.last_import_breakdown && (
        <Panel label="CSV reconciliation · latest import" accent="cyan">
          <CsvBreakdownPanel
            filename={dash.data.sync_health.csv.last_import_filename}
            status={dash.data.sync_health.csv.last_import_status}
            importedAt={dash.data.sync_health.csv.last_import_at}
            breakdown={dash.data.sync_health.csv.last_import_breakdown}
          />
        </Panel>
      )}

      <DailyChangePanel />

      <Panel label="Webhook pipeline integrity · 24h" accent="cyan">
        <WebhookHealthPanel data={webhookHealth.data} />
        {webhookAlerts.data?.history && webhookAlerts.data.history.length > 0 && (
          <AlertHistory alerts={webhookAlerts.data.history} />
        )}
      </Panel>

      <section className="grid grid-cols-3 gap-4">
        <Panel label="Added · 24h" accent="green">
          <Metric label="members" value={data?.stats.last_24h_added} tone="green" size="md" />
        </Panel>
        <Panel label="Removed · 24h" accent="red">
          <Metric label="members" value={data?.stats.last_24h_removed} tone="red" size="md" />
        </Panel>
        <Panel label="Errors · 24h" accent={data?.stats.errors ? 'red' : 'amber'}>
          <Metric
            label="failures"
            value={data?.stats.errors}
            tone={data?.stats.errors ? 'red' : 'paper'}
            size="md"
          />
        </Panel>
      </section>

      <Panel label="Email validation coverage" accent="cyan">
        <ValidationCoveragePanel data={coverage.data} isLoading={coverage.isLoading} />
      </Panel>

      <Panel label="Deliverability (rolling 7d / 30d)" accent="cyan">
        <DeliverabilityPanel data={deliverability.data} isLoading={deliverability.isLoading} />
      </Panel>

      <Panel label="Recent Runs" bodyClassName="p-0" accent="violet">
        {isLoading && (
          <div className="px-4 py-10 text-center label text-paper-trace">Loading…</div>
        )}
        {!isLoading && (data?.items.length ?? 0) === 0 && (
          <div className="px-4 py-10 text-center label text-paper-trace">
            No sync runs yet — scheduler runs every 15 min
          </div>
        )}
        {!isLoading && (data?.items.length ?? 0) > 0 && (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>Started</Th>
                <Th>Age</Th>
                <Th>List</Th>
                <Th align="right">+Added</Th>
                <Th align="right">-Removed</Th>
                <Th>Status</Th>
                <Th>Error</Th>
              </tr>
            </thead>
            <tbody>
              {data!.items.map((r) => (
                <tr key={r.id} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                  <Td mono subtle>{fmtDateTime(r.started_at)}</Td>
                  <Td mono subtle>{fmtRelative(r.started_at)}</Td>
                  <Td mono>{r.list_address || '—'}</Td>
                  <Td mono align="right" className="text-radar-green">+{fmtInt(r.delta_added)}</Td>
                  <Td mono align="right" className="text-radar-red">-{fmtInt(r.delta_removed)}</Td>
                  <Td>
                    <StatusBadge status={r.status} />
                  </Td>
                  <Td mono subtle className="max-w-[280px] truncate">{r.error || '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel label="SimpleCirc Sync" accent="amber">
        <div className="flex items-center justify-between gap-6">
          <div className="space-y-1">
            {exp?.last_export && (
              <div className="font-mono text-[11px] text-paper-dim tabular-nums">
                Last export: {exp.last_export.status}
                {exp.last_export.row_count ? ` · ${fmtInt(exp.last_export.row_count)} rows` : ''}
                {exp.last_export.completed_at ? ` · ${fmtRelative(exp.last_export.completed_at)}` : ''}
              </div>
            )}
            <div className="font-mono text-[11px]">
              {exportInFlight ? (
                <span className="text-amber-glow inline-flex items-center gap-1.5">
                  <StatusDot tone="active" pulse />
                  {reqStatus === 'pending'
                    ? 'queued · picked up within ~30s'
                    : 'running · pulling from SimpleCirc…'}
                </span>
              ) : reqStatus === 'failed' ? (
                <span className="text-radar-red">last run failed: {exp?.request?.error ?? 'unknown error'}</span>
              ) : reqStatus === 'done' ? (
                <span className="text-paper-trace">last manual run: done · {exp?.request?.completed_at ? fmtRelative(exp.request.completed_at) : ''}</span>
              ) : null}
            </div>
            {runExport.isError && (runExport.error as ApiError)?.status === 409 && (
              <div className="font-mono text-[11px] text-amber-glow">
                An export is already {((runExport.error as ApiError).detail as { status?: string })?.status === 'already_running' ? 'running' : 'queued'}.
              </div>
            )}
          </div>
          <Button
            onClick={() => runExport.mutate()}
            disabled={runExport.isPending || exportInFlight}
            size="lg"
          >
            {exportInFlight ? 'Syncing…' : 'Run SimpleCirc Sync Now'}
          </Button>
        </div>
      </Panel>
    </div>
  )
}

function Th({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return <th className={`label py-2 px-4 font-normal text-${align}`}>{children}</th>
}

function Td({
  children, mono, subtle, align = 'left', className = '',
}: {
  children: React.ReactNode
  mono?: boolean
  subtle?: boolean
  align?: 'left' | 'right'
  className?: string
}) {
  return (
    <td
      className={`py-2 px-4 text-${align}
        ${mono ? 'font-mono text-[11px] tabular-nums' : 'text-[13px]'}
        ${subtle ? 'text-paper-dim' : 'text-paper'}
        ${className}`}
    >
      {children}
    </td>
  )
}

function AlertBanner({ alerts }: { alerts: import('../api/client').WebhookAlert[] }) {
  const qc = useQueryClient()
  const ack = useMutation({
    mutationFn: (id: number) => api.post(`/api/sync/webhook-alerts/${id}/acknowledge`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhook_alerts'] }),
  })
  const anyRed = alerts.some((a) => a.level === 'red')
  return (
    <div className={`border p-4 space-y-2 ${anyRed
      ? 'border-radar-red bg-radar-red/10'
      : 'border-amber-glow/60 bg-amber-glow/10'}`}
    >
      <div className="flex items-center gap-2">
        <span className={`font-mono text-[14px] ${anyRed ? 'text-radar-red' : 'text-amber-glow'}`}>
          ⚠ {alerts.length} active webhook alert{alerts.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="space-y-1">
        {alerts.map((a) => (
          <div key={a.id} className="font-mono text-[12px] flex items-start gap-3">
            <span className="flex-1">
              <span className={a.level === 'red' ? 'text-radar-red' : 'text-amber-glow'}>
                [{a.source} · {a.kind}]
              </span>
              {' '}
              <span className="text-paper">{a.message}</span>
              <span className="text-paper-trace"> · raised {fmtRelative(a.raised_at)}</span>
            </span>
            <button
              onClick={() => ack.mutate(a.id)}
              disabled={ack.isPending}
              className="label text-paper-faint hover:text-amber-glow shrink-0"
              title="Resolve this alert. Re-raises if the underlying condition still holds on the next check."
            >
              {ack.isPending && ack.variables === a.id ? 'Ack…' : 'Ack'}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function AlertHistory({ alerts }: { alerts: import('../api/client').WebhookAlert[] }) {
  // Show recent-resolved separately so active alerts aren't duplicated here.
  const resolved = alerts.filter((a) => a.resolved_at).slice(0, 10)
  if (resolved.length === 0) return null
  return (
    <details className="border-t border-ink-500 mt-3 pt-3">
      <summary className="label text-paper-faint cursor-pointer hover:text-amber-glow">
        Resolved incidents · last 72h ({resolved.length})
      </summary>
      <div className="font-mono text-[11px] space-y-0.5 mt-2">
        {resolved.map((a) => (
          <div key={a.id} className="text-paper-dim">
            <span className={a.level === 'red' ? 'text-radar-red' : 'text-amber-glow'}>
              [{a.source}·{a.kind}]
            </span>
            {' '}
            {a.message}
            {' · '}
            <span className="text-paper-trace">
              raised {fmtRelative(a.raised_at)}
              {a.resolved_at && ` · resolved ${fmtRelative(a.resolved_at)}`}
            </span>
          </div>
        ))}
      </div>
    </details>
  )
}


function WebhookHealthPanel({ data }: { data: WebhookHealth | undefined }) {
  if (!data) {
    return <div className="label text-paper-trace">Loading…</div>
  }
  const ordered = ['mailgun', 'simplecirc'].filter((s) => s in data.sources)
  return (
    <div className="space-y-4">
      {ordered.map((src) => {
        const s = data.sources[src]
        const errorRate = s.total > 0
          ? ((s.failed + s.signature_invalid) / s.total) * 100
          : 0
        const tone = s.errors_1h > 0
          ? 'text-radar-red'
          : errorRate > 1
            ? 'text-amber-glow'
            : 'text-radar-green'
        return (
          <div key={src} className="border border-ink-500 bg-ink-900/40 p-3 space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="label-amber">{src}</span>
              <span className={`font-mono text-[11px] ${tone}`}>
                {s.errors_1h > 0
                  ? `⚠ ${s.errors_1h} error(s) in last 1h`
                  : errorRate > 1
                    ? `amber · ${errorRate.toFixed(1)}% error rate (24h)`
                    : 'healthy'}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <WhTile label="total" value={s.total} tone="text-paper" />
              <WhTile label="success" value={s.success} tone="text-radar-green" />
              <WhTile label="failed" value={s.failed} tone={s.failed ? 'text-radar-red' : 'text-paper-trace'} />
              <WhTile label="sig invalid" value={s.signature_invalid} tone={s.signature_invalid ? 'text-radar-red' : 'text-paper-trace'} />
              <WhTile label="duplicate" value={s.duplicate} tone="text-paper-dim" />
            </div>
            <div className="font-mono text-[11px] text-paper-trace flex flex-wrap gap-x-5 gap-y-1 pt-1">
              <span>
                Last arrival:{' '}
                <span className="text-paper">
                  {s.last_arrival_at ? fmtRelative(s.last_arrival_at) : 'never'}
                </span>
              </span>
              {s.failures_by_kind_24h && (s.failures_by_kind_24h.transient + s.failures_by_kind_24h.permanent + s.failures_by_kind_24h.unclassified) > 0 && (
                <span>
                  24h failures:{' '}
                  {s.failures_by_kind_24h.transient > 0 && (
                    <span className="text-amber-glow">
                      {s.failures_by_kind_24h.transient} transient
                    </span>
                  )}
                  {s.failures_by_kind_24h.transient > 0 && (s.failures_by_kind_24h.permanent + s.failures_by_kind_24h.unclassified) > 0 && ' · '}
                  {s.failures_by_kind_24h.permanent > 0 && (
                    <span className="text-radar-red">
                      {s.failures_by_kind_24h.permanent} permanent
                    </span>
                  )}
                  {(s.failures_by_kind_24h.transient + s.failures_by_kind_24h.permanent) > 0 && s.failures_by_kind_24h.unclassified > 0 && ' · '}
                  {s.failures_by_kind_24h.unclassified > 0 && (
                    <span className="text-paper-dim">
                      {s.failures_by_kind_24h.unclassified} unclassified
                    </span>
                  )}
                </span>
              )}
              {s.last_error_at && (
                <span>
                  Last error: <span className="text-radar-red">
                    {s.last_error_type ?? 'unknown'}
                  </span>
                  {' · '}
                  <span className="text-paper">
                    {fmtRelative(s.last_error_at)}
                  </span>
                  {s.last_error && (
                    <span className="text-paper-trace"> — {s.last_error.slice(0, 80)}</span>
                  )}
                </span>
              )}
              {s.pending > 0 && (
                <span className="text-amber-glow">
                  {s.pending} pending (stuck?)
                </span>
              )}
            </div>
          </div>
        )
      })}
      <div className="font-mono text-[10px] text-paper-trace">
        Replay failed rows: <code>docker compose exec api python -m aamcirc.tools.replay_webhooks --status=failed --since=2h</code>
      </div>
    </div>
  )
}

function WhTile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="border border-ink-500 bg-ink-800/40 px-2 py-1.5">
      <div className="label text-paper-faint">{label}</div>
      <div className={`font-mono text-[18px] tabular-nums ${tone}`}>{fmtInt(value)}</div>
    </div>
  )
}

function CsvBreakdownPanel({
  filename, status, importedAt, breakdown,
}: {
  filename: string | null
  status: string | null
  importedAt: string | null
  breakdown: NonNullable<DashboardSnapshot['sync_health']['csv']['last_import_breakdown']>
}) {
  const seen = breakdown.rows_seen
  const imported = breakdown.rows_imported
  // Filter-funnel math: seen minus exclusions equals imported. Showing the
  // diff line makes the reconciliation explicit — ops don't have to subtract
  // in their head to understand where the "missing" rows went.
  const excluded = breakdown.rows_skipped_bad_publication + breakdown.rows_skipped_other
  const deduped = Math.max(0, seen - excluded - imported)
  const statusTone =
    status === 'ok' ? 'text-radar-green' :
    status === 'ok_with_errors' ? 'text-amber-glow' :
    'text-radar-red'

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between font-mono text-[11px]">
        <div className="text-paper-dim">
          {filename || 'no file'}
          {importedAt && <span className="text-paper-trace"> · {fmtRelative(importedAt)}</span>}
          {breakdown.duration_seconds !== null && (
            <span className="text-paper-trace"> · {fmtDuration(breakdown.duration_seconds)}</span>
          )}
        </div>
        {status && <span className={statusTone}>{status}</span>}
      </div>

      {/* Headline funnel: seen → imported */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Tile label="Rows seen" value={fmtInt(seen)} tone="text-paper" sub="CSV lines read" />
        <Tile
          label="Bad publication"
          value={fmtInt(breakdown.rows_skipped_bad_publication)}
          tone={breakdown.rows_skipped_bad_publication ? 'text-paper-dim' : 'text-paper-trace'}
          sub="out-of-scope titles"
        />
        <Tile
          label="Parse-skipped"
          value={fmtInt(breakdown.rows_skipped_other)}
          tone={breakdown.rows_skipped_other ? 'text-amber-glow' : 'text-paper-trace'}
          sub="missing account_id etc."
        />
        <Tile
          label="Missing email"
          value={fmtInt(breakdown.rows_skipped_missing_email)}
          tone="text-paper-dim"
          sub="still imported"
        />
        <Tile
          label="Deduped in batch"
          value={fmtInt(deduped)}
          tone="text-paper-dim"
          sub="same account, multi-pub"
        />
        <Tile label="Imported" value={fmtInt(imported)} tone="text-radar-green" sub="upserted rows" />
      </div>

      {/* Subscriber / subscription split */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Tile
          label="Subscribers upserted"
          value={fmtInt(breakdown.subscribers_upserted)}
          tone="text-paper"
          sub="unique accounts"
        />
        <Tile
          label="Subscriptions upserted"
          value={fmtInt(breakdown.subscriptions_upserted)}
          tone="text-paper"
          sub="account × publication"
        />
        <Tile
          label="Taken-over rows protected"
          value={fmtInt(breakdown.takeover_rows_protected)}
          tone={breakdown.takeover_rows_protected ? 'text-paper' : 'text-paper-trace'}
          sub="we bill these, not SimpleCirc"
        />
      </div>

      <div className="font-mono text-[11px] text-paper-trace border-t border-ink-500 pt-3 flex flex-wrap gap-x-5 gap-y-1">
        <span>
          Funnel:
          {' '}<span className="text-paper">{fmtInt(seen)} seen</span>
          {' − '}<span className="text-paper-dim">{fmtInt(breakdown.rows_skipped_bad_publication + breakdown.rows_skipped_other)} excluded</span>
          {' − '}<span className="text-paper-dim">{fmtInt(deduped)} deduped</span>
          {' = '}<span className="text-radar-green">{fmtInt(imported)} imported</span>
        </span>
        {breakdown.errors_count > 0 && (
          <span className="text-amber-glow">
            {fmtInt(breakdown.errors_count)} row error{breakdown.errors_count === 1 ? '' : 's'} (see csv_imports.summary → errors)
          </span>
        )}
      </div>
    </div>
  )
}

function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s === 0 ? `${m}m` : `${m}m ${s}s`
}

function ValidationCoveragePanel({
  data, isLoading,
}: {
  data: ValidationCoverage | undefined
  isLoading: boolean
}) {
  if (isLoading || !data) {
    return <div className="label text-paper-trace p-4">Loading coverage…</div>
  }
  const coveragePct = (data.coverage * 100).toFixed(2)
  const deliverable = data.by_status['deliverable'] ?? 0
  const catchAll = data.by_status['catch_all'] ?? 0
  const undeliverable = data.by_status['undeliverable'] ?? 0
  const doNotSend = data.by_status['do_not_send'] ?? 0
  const unknownStatus = data.by_status['unknown'] ?? 0
  const coverageOk = data.coverage >= 0.95

  return (
    <div className="space-y-4">
      {/* Headline row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Coverage" value={`${coveragePct}%`} tone={coverageOk ? 'text-radar-green' : 'text-amber-glow'} />
        <Tile label="Validated" value={fmtInt(data.validated)} tone="text-paper" sub={`of ${fmtInt(data.total_with_email)}`} />
        <Tile label="Unvalidated" value={fmtInt(data.unvalidated)} tone={data.unvalidated ? 'text-amber-glow' : 'text-paper-trace'} sub="awaiting sweep" />
        <Tile label="Stale (>TTL)" value={fmtInt(data.stale)} tone={data.stale ? 'text-amber-glow' : 'text-paper-trace'} sub={`> ${data.ttl_days}d`} />
      </div>

      {/* Per-bucket breakdown */}
      <div className="grid grid-cols-5 gap-2">
        <StatusChip label="deliverable" value={deliverable} tone="text-radar-green" />
        <StatusChip label="catch_all" value={catchAll} tone="text-paper" />
        <StatusChip label="unknown" value={unknownStatus} tone="text-paper-dim" />
        <StatusChip label="undeliverable" value={undeliverable} tone="text-radar-red" />
        <StatusChip label="do_not_send" value={doNotSend} tone="text-radar-red" />
      </div>

      <div className="font-mono text-[11px] text-paper-trace border-t border-ink-500 pt-3 flex flex-wrap gap-x-5 gap-y-1">
        <span>
          Last validated: <span className="text-paper">
            {data.last_validated_at ? fmtRelative(data.last_validated_at) : 'never'}
          </span>
        </span>
        <span>
          Sweep cap: <span className="text-paper">{fmtInt(data.sweep_limit_per_night)}/night at 04:30 UTC</span>
        </span>
        <span>
          TTL: <span className="text-paper">{data.ttl_days} days</span>
        </span>
        {data.needs_attention > 0 && (
          <span className="text-amber-glow">
            {fmtInt(data.needs_attention)} addresses queued for next sweep
          </span>
        )}
      </div>
    </div>
  )
}

const pct = (x: number) => (x * 100).toFixed(2) + '%'

// Fixed display order for staleness buckets — backend may emit them in any
// key order, but ops read youngest → oldest → never.
const STALENESS_ORDER = ['<30d', '30-180d', '>180d', 'never'] as const

function rateTone(rate: number): string {
  // Mirror the warn/red thresholds used elsewhere on this page: anything
  // above 5% bounce/complaint is red, above 1% is amber.
  if (rate > 0.05) return 'text-radar-red'
  if (rate > 0.01) return 'text-amber-glow'
  return 'text-paper'
}

function DeliverabilityPanel({
  data, isLoading,
}: {
  data: Deliverability | undefined
  isLoading: boolean
}) {
  if (isLoading || !data) {
    return <div className="label text-paper-trace p-4">Loading deliverability…</div>
  }

  const domains = Object.entries(data.by_domain)
  const titles = Object.entries(data.by_title)
  const campaigns = data.by_campaign

  return (
    <div className="space-y-4">
      {/* Summary rows: 7d and 30d */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <DeliverabilitySummaryRow label="rolling 7d" block={data.window_7d} />
        <DeliverabilitySummaryRow label="rolling 30d" block={data.window_30d} />
      </div>

      {/* By sending domain */}
      <div>
        <div className="label-amber mb-2">By sending domain</div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-ink-500">
              <Th>Domain</Th>
              <Th align="right">Sent</Th>
              <Th align="right">Bounce%</Th>
              <Th align="right">Complaint%</Th>
            </tr>
          </thead>
          <tbody>
            {domains.length === 0 && (
              <tr><Td subtle>No domain data</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td></tr>
            )}
            {domains.map(([domain, b]) => (
              <tr key={domain} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                <Td mono>{domain}</Td>
                <Td mono align="right">{fmtInt(b.sent)}</Td>
                <Td mono align="right" className={rateTone(b.bounce_rate)}>{pct(b.bounce_rate)}</Td>
                <Td mono align="right" className={rateTone(b.complaint_rate)}>{pct(b.complaint_rate)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* By validation staleness — fixed bucket order */}
      <div>
        <div className="label-amber mb-2">By validation staleness</div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-ink-500">
              <Th>Bucket</Th>
              <Th align="right">Sent</Th>
              <Th align="right">Bounce%</Th>
            </tr>
          </thead>
          <tbody>
            {STALENESS_ORDER.map((bucket) => {
              const b = data.by_staleness[bucket]
              return (
                <tr key={bucket} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                  <Td mono>{bucket}</Td>
                  <Td mono align="right">{b ? fmtInt(b.sent) : '—'}</Td>
                  <Td mono align="right" className={b ? rateTone(b.bounce_rate) : ''}>{b ? pct(b.bounce_rate) : '—'}</Td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* By title */}
      <div>
        <div className="label-amber mb-2">By title</div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-ink-500">
              <Th>Title</Th>
              <Th align="right">Sent</Th>
              <Th align="right">Bounce%</Th>
              <Th align="right">Complaint%</Th>
            </tr>
          </thead>
          <tbody>
            {titles.length === 0 && (
              <tr><Td subtle>No title data</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td></tr>
            )}
            {titles.map(([title, b]) => (
              <tr key={title} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                <Td mono>{title}</Td>
                <Td mono align="right">{fmtInt(b.sent)}</Td>
                <Td mono align="right" className={rateTone(b.bounce_rate)}>{pct(b.bounce_rate)}</Td>
                <Td mono align="right" className={rateTone(b.complaint_rate)}>{pct(b.complaint_rate)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per campaign — already sorted worst-bounce-first */}
      <div>
        <div className="label-amber mb-2">Per campaign</div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-ink-500">
              <Th>Name</Th>
              <Th align="right">Sent</Th>
              <Th align="right">Delivered</Th>
              <Th align="right">Bounce%</Th>
              <Th align="right">Complaint%</Th>
              <Th align="right">Unsubs</Th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && (
              <tr><Td subtle>No campaign data</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td></tr>
            )}
            {campaigns.map((c) => (
              <tr key={c.campaign_id} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                <Td>{c.name}</Td>
                <Td mono align="right">{fmtInt(c.sent)}</Td>
                <Td mono align="right">{fmtInt(c.delivered)}</Td>
                <Td mono align="right" className={rateTone(c.bounce_rate)}>{pct(c.bounce_rate)}</Td>
                <Td mono align="right" className={rateTone(c.complaint_rate)}>{pct(c.complaint_rate)}</Td>
                <Td mono align="right">{fmtInt(c.unsubs)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DeliverabilitySummaryRow({ label, block }: { label: string; block: DeliverabilityRateBlock }) {
  return (
    <div className="border border-ink-500 bg-ink-900/40 p-3 space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="label-amber">{label}</span>
        <span className="font-mono text-[11px] text-paper-trace">
          {fmtInt(block.sent)} sent · {fmtInt(block.delivered)} delivered
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <StatusChip label="bounce" value={block.bounce_rate} tone={rateTone(block.bounce_rate)} format="pct" />
        <StatusChip label="complaint" value={block.complaint_rate} tone={rateTone(block.complaint_rate)} format="pct" />
        <StatusChip label="unsub" value={block.unsub_rate} tone="text-paper-dim" format="pct" />
      </div>
    </div>
  )
}

function Tile({ label, value, tone, sub }: { label: string; value: string; tone: string; sub?: string }) {
  return (
    <div className="border border-ink-500 bg-ink-800/40 p-3">
      <div className="label text-paper-faint">{label}</div>
      <div className={`font-mono text-[24px] tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-paper-trace mt-0.5">{sub}</div>}
    </div>
  )
}

function StatusChip({ label, value, tone, format = 'int' }: { label: string; value: number; tone: string; format?: 'int' | 'pct' }) {
  return (
    <div className="border border-ink-500 bg-ink-900/40 px-2 py-1.5">
      <div className={`label ${tone}`}>{label}</div>
      <div className={`font-mono text-[14px] tabular-nums ${format === 'pct' ? tone : 'text-paper'}`}>
        {format === 'pct' ? pct(value) : fmtInt(value)}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'ok' ? 'ok' :
    status === 'error' ? 'err' :
    status === 'running' ? 'active' :
    status === 'skipped_no_list' ? 'muted' : 'warn'
  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusDot tone={tone} pulse={tone === 'active'} />
      <span className="label">{status}</span>
    </span>
  )
}
